import { UserSwitchOutlined } from "@ant-design/icons";
import { Flex } from "antd";
import dayjs from "dayjs";
import { useCallback, useMemo, useState } from "react";
import { getTaskListsThunk, getWorkGetScheduleThunk } from "tm-shared/api";
import { AnyTask, Point, WorkStatus } from "tm-shared/types";

import { ROUTES } from "@/constants";
import { ROLE_CHILDREN_MAP } from "@/constants/domain/managerAgentVocabulary";
import { useAsyncAction, useQueueInfo } from "@/hooks";
import { useAsyncTaskQueue } from "@/hooks/core/useAsyncTaskQueue";
import { useGlobalCooldown } from "@/hooks/core/useGlobalCooldown";
import {
    useAppDispatch,
    useAppSelector,
    useOfflineModeSelector,
    useProtectedUserRoleSelector,
} from "@/hooks/redux";
import { Header } from "@/shared/layout";
import { MainContainer } from "@/shared/layout/Layout";
import { HeaderNavLink } from "@/shared/navigation/HeaderNavLink";
import { HeaderReloadBtn } from "@/shared/navigation/HeaderReloadBtn";
import { Meta } from "@/shared/seo";
import { isManagerRole } from "@/utils/business/isManagerRole";

import { ManagerEmptyStateBlock } from "./blocks/ManagerEmptyStateBlock";
import { WorkControlsBlock } from "./blocks/WorkControlsBlock";
import { WorkPlanModals } from "./modals/WorkPlanModals";
import { PointsSection } from "./sections/PointsSection";
import { WorkPlanProvider } from "./WorkPlan.context";
import { getWorkPlanCapabilities } from "./WorkPlan.policy";
import { WorkPlanModalType, WorkPlanTasksInfoModal } from "./WorkPlan.types";

const COOLDOWN_DURATION = 30_000;

export const WorkPlanPage = () => {
    const dispatch = useAppDispatch();

    const [modal, setModal] = useState<WorkPlanModalType>(null);
    const [tasksInfoModal, setTasksInfoModal] =
        useState<WorkPlanTasksInfoModal | null>(null);

    const role = useProtectedUserRoleSelector();
    const capabilities = getWorkPlanCapabilities(role);
    const userId = useAppSelector((s) => s.user.id);
    const offlineMode = useOfflineModeSelector();

    const workStatus = useAppSelector((s) => s.work.workStatus);
    const activeSchedule = useAppSelector((s) => s.manager.activeSchedule);
    const lastUpdated = useAppSelector((s) => s.manager.lastUpdated);
    const isUnscheduledPointsProcessing = useAppSelector(
        (s) => s.offline.isUnscheduledPointsProcessing,
    );

    const { queue, progress: queueProgress } = useAsyncTaskQueue();
    const { hasPending } = useQueueInfo();
    const { disabled: isCooldownActive, trigger: triggerCooldown } =
        useGlobalCooldown(COOLDOWN_DURATION);

    const isManager = isManagerRole(role);
    const isWorking = isManager || workStatus === WorkStatus.StartWork;

    const isScheduleEmpty =
        !activeSchedule ||
        !dayjs(lastUpdated.activeSchedule, "DD.MM.YYYY HH:mm:ss").isSame(
            dayjs(),
            "day",
        );

    const isEmptyManagerWorkPlan =
        capabilities.canSeeManagerEmptyState && isScheduleEmpty;

    const isReloadDisabled =
        isCooldownActive ||
        offlineMode ||
        hasPending ||
        isUnscheduledPointsProcessing;

    const [handleSchedulesReload, isReloadLoading] = useAsyncAction(
        async () => {
            triggerCooldown();

            const roleSlug = capabilities.canWorkWithConnectedAgentSchedule
                ? ROLE_CHILDREN_MAP[role]
                : undefined;
            const scheduleUserId =
                capabilities.canWorkWithConnectedAgentSchedule
                    ? activeSchedule?.userId
                    : userId;

            const { result } = await dispatch(
                getWorkGetScheduleThunk({
                    userId: scheduleUserId,
                    roleSlug,
                }),
            ).unwrap();

            const pointIds = [
                ...result.dayPointScheduleExtra,
                ...Object.keys(result.points),
            ];

            await dispatch(
                getTaskListsThunk({
                    targetIds: pointIds,
                    roleSlug,
                }),
            );
        },
    );

    const handleCloseModal = useCallback(() => {
        setModal(null);
    }, []);

    const handleOpenDayStartedModal = useCallback(() => {
        setModal("dayStarted");
    }, []);

    const handleOpenForceStopWorkModal = useCallback(() => {
        setModal("forceStopWork");
    }, []);

    const handleOpenTasksInfoModal = useCallback(
        ({ point, task }: { point: Point; task: AnyTask }) => {
            setTasksInfoModal({ point, task });
        },
        [],
    );

    const handleCloseTasksInfoModal = useCallback(() => {
        setTasksInfoModal(null);
    }, []);

    const headerActions = useMemo(
        () => (
            <>
                <HeaderNavLink to={ROUTES.AssignmentsPage} end>
                    <UserSwitchOutlined />
                </HeaderNavLink>

                <HeaderReloadBtn
                    onClick={handleSchedulesReload}
                    disabled={isReloadDisabled}
                />
            </>
        ),
        [handleSchedulesReload, isReloadDisabled],
    );

    if (isEmptyManagerWorkPlan) {
        return (
            <>
                <Header />

                <MainContainer>
                    <ManagerEmptyStateBlock />
                </MainContainer>
            </>
        );
    }

    return (
        <WorkPlanProvider value={{ capabilities }}>
            <>
                <Meta title="Робочий план" />

                <Header
                    withMidnightCountdown
                    titleLoading={isReloadLoading}
                    rightActions={headerActions}
                />

                <MainContainer>
                    <Flex vertical gap={16}>
                        <PointsSection
                            isWorking={isWorking}
                            onOpenTasksInfoModal={handleOpenTasksInfoModal}
                        />

                        <WorkControlsBlock
                            queue={queue}
                            openDayStartedModal={handleOpenDayStartedModal}
                            openForceStopWorkModal={
                                handleOpenForceStopWorkModal
                            }
                        />
                    </Flex>
                </MainContainer>

                <WorkPlanModals
                    modal={modal}
                    tasksInfoModal={tasksInfoModal}
                    queueProgress={queueProgress}
                    onCloseModal={handleCloseModal}
                    onCloseTasksInfoModal={handleCloseTasksInfoModal}
                />
            </>
        </WorkPlanProvider>
    );
};

export default WorkPlanPage;
